<template>
  <article class="report">
    <header class="report__header">
      <h3 class="section__subtitle">
        Inadimplência
      </h3>
    </header>

    <div class="section__wrapper report__wrapper">
      <div
        v-if="filters.length > 0"
        class="report__filters"
      >
        <div
          v-for="(filter, index) in filters"
          :key="`report_filter_${index}`"
          class="report__filters-item"
        >
          <label class="report__filters-label">
            {{ filter.label }}
          </label>

          <template v-if="filter.type === 'date'">
            <div
              v-click-outside="() => filter.showCalendar = false"
              class="report__filters-field"
            >
              <div
                :class="filter.showCalendar && 'active'"
                class="report__filters-date"
                @click="filter.showCalendar = !filter.showCalendar"
              >
                <span>
                  {{ $moment(filter.value).format('DD/MM/YYYY') }}
                </span>

                <dr-icon
                  :color="'#8E8E92'"
                  class="agenda-settings__date-picker--icon"
                  name="dashCalendar"
                />
              </div>

              <div class="report__filters-calendar">
                <dr-calendar
                  v-show="filter.showCalendar"
                  v-model.lazy="filter.value"
                  @change="filter.showCalendar = false"
                />
              </div>
            </div>
          </template>

          <template v-if="filter.type === 'text'">
            <div class="report__filters-field">
              <input
                v-model.lazy="filter.value"
                :placeholder="filter.placeholder"
                type="text"
                class="report__filters-text"
              >

              <i
                v-show="filter.value"
                class="icon icon-cross report__filters-reset"
                @click="filter.value = null"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="report__body">
        <dr-loader
          v-if="ui.isLoading"
          size="lg"
          class="report__loader"
        />

        <p
          v-if="!ui.isLoading && Array.isArray(data.rows) && data.rows.length === 0"
          class="report__no-results"
        >
          Nenhum resultado encontrado.
        </p>

        <template
          v-if="!ui.isLoading && Array.isArray(data.rows) && data.rows.length > 0"
          tag="div"
          class="report__table"
        >
          <header
            v-if="fn_IS_MOBILE()"
            class="report__table-header mobile"
            :class="$store.getters.getNewCertifierCredentialRequired ? 'withCredentials' : '' ">
            <div
              :class="data.keys[0].class"
              class="report__table-col"
            >
              {{ data.keys[0].title }}
            </div>
            <div class="report__table-col">
              <select v-model="ui.selectedCol">
                <template v-for="(key, index) in data.keys">
                  <option
                    v-if="index > 0 && key.name !== 'actions'"
                    :value="key.name" :key="`option${key.title}`">{{ key.title }}</option>
                </template>
              </select>
            </div>
          </header>
          <div v-else class="report__table-header">
            <div
              v-for="(key, index) in data.keys"
              :key="`report_header_item_${index}`"
              :class="key.class"
              class="report__table-col"
            >
              {{ key.title }}
            </div>
          </div>

          <template v-if="fn_IS_MOBILE()">
            <div
              v-for="(row, index) in getRows"
              :key="`report_table_row_${index}`"
              class="report__table-row"
            >
              <template v-for="(cell, i) in row">
                <div
                  v-if="cell.name === 'name' || cell.name === getSelectedCol"
                  :key="`report_table_row_${index}_cell_${i}`"
                  class="report__table-cell"
                  :class="cell.name === 'name' ? 'report__table-cell--highlight' : ''"
                >
                  <span>
                    {{ returnWithFilter(cell) }}
                  </span>
                </div>
              </template>
            </div>
          </template>
          <template v-else>
            <div
              v-for="(row, index) in data.rows"
              :key="`report_table_row_${index}`"
              class="report__table-row"
            >
              <div
                v-for="(cell, i) in row"
                :key="`report_table_row_${index}_cell_${i}`"
                :class="cell.class"
                class="report__table-cell"
              >
                <span
                  v-if="cell.name === 'name'"
                  class="report__table-cell--highlight"
                >
                  {{ cell.value || '(não informado)' }}
                </span>

                <span v-else-if="cell.name === 'type'">
                  {{ cell.value | APPOINTMENT_TYPE }}
                </span>

                <span v-else-if="cell.name === 'value'">
                  R$ {{ parseFloat(cell.value) | TO_BRL }}
                </span>

                <span v-else-if="cell.name === 'phone'">
                  {{ cell.value || '(não informado)' | TO_TEL }}
                </span>

                <span v-else-if="cell.name === 'date'">
                  {{ cell.value | TO_DATE_TIME }}
                </span>

                <span v-else>
                  {{ cell.value || '(não informado)' | TO_ALL_LOWER }}
                </span>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>

    <footer
      v-if="ui.hasLoaded && pagination.totalPages > 1"
      class="section__footer"
    >
      <nav class="pagination">
        <transition name="fade" mode="out-in">
          <dr-table-pagination
            :total-pages.sync="pagination.totalPages"
            :current-page.sync="pagination.currentPage"
            class="text-right"
            @updated="getReport($event)"
          />
        </transition>
      </nav>
    </footer>
  </article>
</template>

<script>
import DrTablePagination from '@/components/tables/tablePagination'
import { DrCalendar } from 'cm2tech-vue-calendar'
import { mapGetters } from 'vuex'

export default {
  name: 'DrReportsExamsProcedures',

  components: {
    DrTablePagination,
    DrCalendar
  },

  data () {
    return {
      data: {
        keys: [
          {
            title: 'Nome',
            name: 'name',
            class: 'report__table-col--20'
          },
          {
            title: 'Tipo',
            name: 'type',
            class: 'report__table-col--20'
          },
          {
            title: 'Valor',
            name: 'value',
            class: 'report__table-col--15'
          },
          {
            title: 'E-mail',
            name: 'email',
            class: 'report__table-col--15'
          },
          {
            title: 'Telefone',
            name: 'phone',
            class: 'report__table-col--15'
          },
          {
            title: 'Data',
            name: 'date',
            class: 'report__table-col--15'
          }
        ],
        rows: null
      },
      filters: [
        {
          name: 'startDate',
          label: 'Data inicial',
          type: 'date',
          queryString: '&filterAndGreaterThanOrEqual[a.appointmentStart]=',
          value: this.$moment().startOf('month').toISOString(),
          showCalendar: false
        },
        {
          name: 'endDate',
          label: 'Data final',
          type: 'date',
          queryString: '&filterAndLessThanOrEqual[a.appointmentFinish]=',
          value: null,
          showCalendar: false
        },
        {
          name: 'name',
          label: 'Filtrar por nome',
          placeholder: 'Digite o nome',
          type: 'text',
          queryString: '&filterAndLike[sp.name]=',
          value: null
        }
      ],
      pagination: {
        totalRows: null,
        totalPages: null,
        currentPage: null
      },
      itemsPerPage: 10,
      ui: {
        isLoading: true,
        hasLoaded: false,
        selectedCol: 'phone'
      }
    }
  },

  computed: {
    ...mapGetters({
      office: 'getCurrentOffice'
    }),

    endpoint () {
      return `/report/doctor_office/${this.office.id}/unpaid`
    },

    queryString () {
      const query = this.filters.reduce((prev, next, i) => {
        const value = next.type === 'date'
          ? this.$moment(next.value).format('YYYY-MM-DD')
          : next.value

        return next.value
          ? `${prev}${next.queryString}${value}`
          : prev
      }, '')

      return `${query}&filterNot[fs.status]=CANCELADO`
    },

    getRows () {
      let arr = []
      for (let row of this.data.rows) {
        let newRow = {}
        for (let col in row) {
          if (this.getSelectedCol && this.fn_IS_MOBILE()) {
            if (row[col].name === this.getSelectedCol || row[col].name === 'name') {
              newRow[col] = row[col]
              if (!newRow[col].value) {
                newRow[col].value = '(não informado)'
              }
            }
          } else {
            newRow[col] = row[col]
            if (!newRow[col].value) {
              newRow[col].value = '(não informado)'
            }
          }
        }
        arr.push(newRow)
      }
      console.log(arr)
      return arr
    },

    getSelectedCol () {
      return this.ui.selectedCol
    }

  },

  watch: {
    filters: {
      deep: true,
      immediate: false,
      handler (next, prev) {
        const startDate = next.find(f => f.name === 'startDate')
        const endDate = next.find(f => f.name === 'endDate')

        if (this.$moment(startDate.value).isAfter(this.$moment(endDate.value), 'day')) {
          endDate.value = startDate.value
        }
      }
    },

    queryString: {
      deep: false,
      immediate: false,
      handler (next, prev) {
        if (prev !== next) {
          this.getReport()
        }
      }
    },

    'office.id': {
      deep: false,
      immediate: false,
      handler () {
        this.getReport()
      }
    }
  },

  created () {
    this.getReport()
  },

  methods: {
    getReport (page = 1) {
      const url = `${this.endpoint}?itensPerPage=${this.itemsPerPage}${this.queryString}&page=${page}`

      this.ui.isLoading = true
      this.fn_SHOW_PROGRESS()
      this.$http
        .get(url, this.$store.getters.getAuthHeader)
        .then((res) => {
          this.pagination = res.data.pagination

          const data = res.data.data
            .map(item => Object.entries(item))
            .map(item => {
              return item.map((cell, index) => ({
                name: cell[0],
                value: cell[1]
              }))
            })
            .map(this.sortColumns)

          this.data.rows = data
        })
        .finally(() => {
          this.ui.isLoading = false
          this.ui.hasLoaded = true
          this.fn_HIDE_PROGRESS()
        })
    },

    sortColumns (arr = []) {
      const sortedArr = []

      if (arr.length > 0) {
        sortedArr.push({ ...arr.find(i => i.name === 'name'), class: 'report__table-cell--20' })
        sortedArr.push({ ...arr.find(i => i.name === 'type'), class: 'report__table-cell--20' })
        sortedArr.push({ ...arr.find(i => i.name === 'value'), class: 'report__table-cell--15' })
        sortedArr.push({ ...arr.find(i => i.name === 'email'), class: 'report__table-cell--15' })
        sortedArr.push({ ...arr.find(i => i.name === 'phone'), class: 'report__table-cell--15' })
        sortedArr.push({ ...arr.find(i => i.name === 'date'), class: 'report__table-cell--15' })
      }

      return sortedArr
    },

    returnWithFilter (cell) {
      let str = ''
      switch (cell.name) {
        case 'type': str = this.$options.filters.APPOINTMENT_TYPE(cell.value); break
        case 'value': str = `R$ ${this.$options.filters.TO_BRL(parseFloat(cell.value))}`; break
        case 'phone': str = this.$options.filters.TO_TEL(cell.value); break
        case 'date': str = this.$options.filters.TO_DATE_TIME(cell.value); break
        default: str = cell.value
      }
      return str
    }
  }
}
</script>
